import { FC, useCallback, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import {
  Input,
  Select,
  Label,
  Button,
  Radio,
} from "../../../components/common";
import { FormErrorMessage } from "../../../components/messages";
import { PAYER_RELATIONSHIP_CONFIG } from "../../../constants";
import { useCreatePublicPaymentRequestMutation } from "../../../store/services/paymentRequests";
import {
  PayerRelationshipEnum,
  PayerTypeEnum,
  PublicPaymentRequest,
} from "../../../types/PaymentRequestTypes";
import { useSelector } from "react-redux";
import {
  selectPaymentRequestLinks,
  selectSelectedCalculatorInput,
} from "../../../store/reducers/paymentRequestLink";
import { useLocalStorage } from "../../../utils/hooks/useLocalStorage";
import { transformAmount } from "../../../utils/invoice";
import { CURRENCY_MAP } from "../../../constants";

type Props = {
  callback?: (data?: any) => void;
};

type Inputs = {
  payer_type: PayerTypeEnum;
  payer_email: string;
  payer_first_name: string;
  payer_last_name: string;
  payer_phone: string;
  payer_relationship?: PayerRelationshipEnum;
  payer_relationship_other?: string;
  payer_company_name?: string;
  payer_tax_id?: string;
  payer_address?: string;
};

const PAYER_RELATIONSHIP_OPTIONS = PAYER_RELATIONSHIP_CONFIG.map(
  ({ value, label }) => ({
    value: value.toString(),
    label,
  })
);

const PAYER_TYPE_OPTIONS = [
  { value: PayerTypeEnum.INDIVIDUAL, label: "Individual" },
  { value: PayerTypeEnum.BUSINESS, label: "Business" },
];

export const PayerDataForm: FC<Props> = ({ callback }) => {
  const paymentRequestLink = useSelector(selectPaymentRequestLinks);
  const selectedCalculatorInput = useSelector(selectSelectedCalculatorInput);

  const { data: storedPublicPaymentRequest, set: setSessionStorage } =
    useLocalStorage<Partial<PublicPaymentRequest>>("publicPaymentRequest");

  const {
    handleSubmit,
    control,
    formState: { isValid },
    getValues,
    watch,
  } = useForm<Inputs>({
    defaultValues: {
      payer_type:
        storedPublicPaymentRequest?.payer_type || PayerTypeEnum.INDIVIDUAL,
      payer_email: storedPublicPaymentRequest?.payer_email || "",
      payer_first_name: storedPublicPaymentRequest?.payer_first_name || "",
      payer_last_name: storedPublicPaymentRequest?.payer_last_name || "",
      payer_phone: storedPublicPaymentRequest?.payer_phone || "",
      payer_relationship:
        storedPublicPaymentRequest?.payer_relationship || undefined,
      payer_relationship_other:
        storedPublicPaymentRequest?.payer_relationship_other || "",
      payer_company_name: storedPublicPaymentRequest?.payer_company_name || "",
      payer_tax_id: storedPublicPaymentRequest?.payer_tax_id || "",
      payer_address: storedPublicPaymentRequest?.payer_address || "",
    },
  });

  const payerRelationship = watch("payer_relationship");
  const payerType = watch("payer_type");
  const [createPublicPaymentRequest, { data, isSuccess, isError, error }] =
    useCreatePublicPaymentRequestMutation();

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (formData) => {
      try {
        if (!paymentRequestLink?.id) {
          throw new Error("Invalid payment request");
        }

        const currencyDetails = CURRENCY_MAP[selectedCalculatorInput!.currency];

        createPublicPaymentRequest({
          payment_request_link: paymentRequestLink?.id,
          currency: selectedCalculatorInput?.currency,
          amount_atomic_expected: transformAmount(
            "encode",
            Number(selectedCalculatorInput!.amount),
            currencyDetails
          ).toString(),
          ...formData,
        });
      } catch (error) {
        throw error;
      }
    },
    [
      createPublicPaymentRequest,
      paymentRequestLink?.id,
      selectedCalculatorInput,
    ]
  );

  useEffect(() => {
    if (isSuccess && data) {
      setSessionStorage(data);
      callback && callback(data);
    }
  }, [isSuccess, callback, data, setSessionStorage]);

  useEffect(() => {
    return () => {
      if (!data) {
        setSessionStorage({
          payment_request_link: paymentRequestLink?.id,
          ...getValues(),
        });
      }
    };
  }, [data, getValues, paymentRequestLink?.id, setSessionStorage]);

  return (
    <form
      className="flex flex-col gap-6 md:gap-8"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Label label="I'm paying as a">
        <Controller
          name="payer_type"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Radio
              options={PAYER_TYPE_OPTIONS}
              {...field}
              checked={field.value}
            />
          )}
        />
      </Label>
      <Label label="Your email (Optional)">
        <Controller
          name="payer_email"
          control={control}
          render={({ field }) => <Input type="text" {...field} />}
        />
      </Label>

      {payerType === PayerTypeEnum.INDIVIDUAL ? (
        <>
          <Label label="Your first name">
            <Controller
              name="payer_first_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => <Input type="text" {...field} />}
            />
          </Label>

          <Label label="Your last name">
            <Controller
              name="payer_last_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  type="text"
                  note="Your name should match the name of your bank account"
                  {...field}
                />
              )}
            />
          </Label>
        </>
      ) : (
        <Label label="Company name">
          <Controller
            name="payer_company_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                type="text"
                note="Should match name of your bank account"
                {...field}
              />
            )}
          />
        </Label>
      )}

      <Label label="Your phone">
        <Controller
          name="payer_phone"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input type="text" note="For example: +11234567890" {...field} />
          )}
        />
      </Label>

      {payerType === PayerTypeEnum.BUSINESS && (
        <>
          <Label label="Tax ID">
            <Controller
              name="payer_tax_id"
              control={control}
              rules={{ required: true }}
              render={({ field }) => <Input type="text" {...field} />}
            />
          </Label>

          <Label label="Business address">
            <Controller
              name="payer_address"
              control={control}
              rules={{ required: true }}
              render={({ field }) => <Input type="text" {...field} />}
            />
          </Label>
        </>
      )}

      <Label label="Relationship">
        <Controller
          name="payer_relationship"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              placeholder="Your relationship with the receiver"
              options={PAYER_RELATIONSHIP_OPTIONS}
            />
          )}
        />
      </Label>

      {(payerRelationship === PayerRelationshipEnum.OTHER ||
        payerRelationship ===
          PayerRelationshipEnum.BUSINESS_EXPENSES_AND_INVESTMENTS) && (
        <Controller
          name="payer_relationship_other"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              placeholder={
                payerRelationship ===
                PayerRelationshipEnum.BUSINESS_EXPENSES_AND_INVESTMENTS
                  ? "Describe what you are paying for"
                  : "Describe your relationship"
              }
              {...field}
            />
          )}
        />
      )}

      <div className="flex bg-brown-50 p-4 rounded-2xl">
        <div className="basis-1/2 flex flex-col gap-2">
          <div className="font-poppins text-base font-bold uppercase">
            {selectedCalculatorInput?.amount}{" "}
            {selectedCalculatorInput?.currency}
          </div>

          <div className="text-sm text-violet-300 font-semibold">
            Amount to pay
          </div>
        </div>
      </div>

      <FormErrorMessage className="mb-6" isError={isError} error={error} />

      <div className="flex flex-col items-center pb-0 h-18 !z-[50] bg-white">
        <Button
          block
          type="submit"
          variant={Button.variant.PRIMARY}
          disabled={!isValid}
        >
          Continue
        </Button>
      </div>
    </form>
  );
};
